import { FC, Fragment, useContext, useState } from 'react';
import { GoToRoomDetailsParams, Hotel, HotelStatistics } from 'ts/hotel/interfaces/HotelResponse';
import HotelsMap from '../Maps/HotelsMap';
import { FilterModel, LocationValue, RoomTypeGrp } from 'ts/hotel/classes/FilterModel';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import FilterByRoomTypeModal from './Filters/FilterByRoomAI';
import { useTranslation } from 'react-i18next';
import Rating from 'components/ui/Rating/Rating';
import HotelCardRooms from './Cards/HotelCardRooms';
import HotelInfo from './Cards/Hotelnfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { getRoomTypesArr } from 'utils/hotel';
import { dateFormat } from 'utils/shared/utils';
import { useClickAway } from 'hooks/useClickAway';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import ResultHotelTabs from './ResultHotelTabs';
import { MainContext } from 'contexts/MainContext';

interface ResultHotelConfigProps {
  hotels?: Hotel[];
  onFilterChange: (filter: FilterModel) => void;
  onLocationChange: (l: LocationValue) => void;
  isMainLoading: boolean;
  goToDetails: (params: GoToRoomDetailsParams) => void;
  stat?: HotelStatistics;
  poolingFinished?: boolean;
  hideTabs?: boolean;
}

const ResultHotelConfig: FC<ResultHotelConfigProps> = ({
  hotels,
  onFilterChange,
  onLocationChange,
  isMainLoading,
  poolingFinished,
  ...props
}) => {
  const { t } = useTranslation();
  const { activeTab, activeHotel, areActiveRooms, setAreActiveRooms } =
    useContext(MapHotelContext);
  const { filter } = useContext(HotelFilterContext);
  const { userSiteConfig } = useContext(MainContext);
  const [openQuery, setOpenQuery] = useState(false);
  const [openGeoSearch, setOpenGeoSearch] = useState(false);
  const popupRef = useClickAway(() => setAreActiveRooms?.(false), ['hotel-result-filter-row']);

  const onChangeRoom = (group: RoomTypeGrp) => {
    const newFilter = { ...filter! };

    if (newFilter.roomTypes) {
      const roomTypeIndex = newFilter.roomTypes.findIndex((it) => it.title === group.title);

      const newRoomTypes = [...newFilter.roomTypes];
      newRoomTypes[roomTypeIndex] = group;

      const result = {
        ...newFilter,
        roomTypes: newRoomTypes,
        roomTypesArr: getRoomTypesArr(newRoomTypes),
      };
      onFilterChange(result);
    }
  };

  return (
    <Fragment>
      {areActiveRooms &&
        activeHotel &&
        userSiteConfig.hotel.showRoomsInTab &&
        userSiteConfig.hotel.displayStyle === '2' && (
          <div className="hotel-result-settings-content" ref={popupRef}>
            <div className="flex-between">
              <div className="title mr-10 text-uppercase mb-3 flex-row pr-rooms">
                <span>{activeHotel.item.name}</span>


              </div>
              <FontAwesomeIcon
                className="cur-poi"
                icon={faTimes}
                onClick={() => setAreActiveRooms?.(false)}
              />
            </div>
            <HotelCardRooms
              rooms={activeHotel.roomClasses}
              showRooms={true}
              hotel={activeHotel}
              goToDetails={props.goToDetails}
            />
          </div>
        )}
      <div className="hotel-result-settings" onClick={onClickOverlay}>
        <div className="results-responsive-header">
          <span className="title text-md">{t('actions')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        {!props.hideTabs && (
          <ResultHotelTabs
            hotels={hotels}
            isMainLoading={isMainLoading}
            poolingFinished={poolingFinished}
            stat={props.stat}
          />
        )}

        {(activeTab === 0 || (activeTab === 6 && !props.hideTabs)) && (
          <div className="map-wr" style={{ marginTop: 7 }}>
            <HotelsMap
              hotels={hotels}
              onFilterChange={onFilterChange}
              onLocationChange={onLocationChange}
              searchToken={props.stat?.searchToken || ''}
            />
          </div>
        )}
        {activeTab === 1 && (
          <FilterByRoomTypeModal onChangeRoom={onChangeRoom} onChangeFilter={onFilterChange} />
        )}
        {activeTab === 3 &&
          activeHotel &&
          userSiteConfig.hotel.showRoomsInTab &&
          userSiteConfig.hotel.displayStyle !== '2' && (
            <div className="hotel-result-settings-content">
              <div className="display-flex">
                <div className="title mr-10 text-uppercase">{activeHotel.item?.name}</div>
                <Rating value={activeHotel.item?.star || 0} color="#4b71a3" />
              </div>
              <div>{activeHotel.addressInfo?.address}</div>
              <HotelCardRooms
                rooms={activeHotel.roomClasses}
                showRooms={true}
                hotel={activeHotel}
                goToDetails={props.goToDetails}
              />
            </div>
          )}
        {activeTab === 5 && filter && <HotelInfo />}
        {activeTab === 4 && props.stat && (
          <div className="hotel-result-settings-content">
            {!!activeHotel && filter && (
              <Fragment>
                <div className="title mr-10 text-uppercase">{activeHotel.item?.name}</div>
                <Rating value={activeHotel.item?.star || 0} color="#4b71a3" />
                <div className="mt-5">{activeHotel.addressInfo?.address}</div>
                <div className="mt-5 ml-a mr-10">{activeHotel.item?.bpid}</div>
              </Fragment>
            )}
            <div className="mt-5 ml-a mr-10">Sys Token - {props.stat.sysToken}</div>
            <div className="mt-5 ml-a mr-10">Search Token - {props.stat.searchToken}</div>
            <div className="mt-10">
              <FontAwesomeIcon
                icon={faGlobe}
                color="var(--color-contrast)"
                className={props.stat.serp.isLoading ? 'btn-pulse' : ''}
              />
              <span className="ph-10">Total - {props.stat.serp.totalResults}</span>
              <span>
                Matched - {props.stat.serp.green + props.stat.serp.orange + props.stat.serp.red}{' '}
              </span>

              <div className="mt-10">
                <FontAwesomeIcon icon={faGlobe} color="green" />
                <span className="ph-10">{props.stat.serp.green}</span>
                <FontAwesomeIcon icon={faGlobe} color="red" />
                <span className="ph-10">{props.stat.serp.red}</span>
                <FontAwesomeIcon icon={faGlobe} color="orange" />
                <span className="ph-10">{props.stat.serp.orange}</span>
              </div>
              {!!props.stat.query && (
                <div className="mt-10">
                  <div
                    className="cur-poi mb-10 btn-lnk text-underline"
                    onClick={() => setOpenQuery(!openQuery)}
                  >
                    {openQuery ? 'Hide Query' : 'Show Query'}
                  </div>
                  {openQuery && (
                    <div className=" hotel-json">
                      <pre>{props.stat.query}</pre>
                    </div>
                  )}
                </div>
              )}
              {!!props.stat.geoSearch && (
                <div className="mt-10">
                  <div
                    className="cur-poi mb-10 btn-lnk text-underline"
                    onClick={() => setOpenGeoSearch(!openGeoSearch)}
                  >
                    {openGeoSearch ? 'Hide Geo Search' : 'Show Geo Search'}
                  </div>
                  {openGeoSearch && (
                    <div className="hotel-json">
                      <pre>{JSON.stringify(props.stat.geoSearch, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
              {!!props.stat.searchLogs?.length && (
                <div className="mt-10">
                  <div className="title mr-10 text-uppercase">Search Logs</div>
                  <div className="hotel-json">
                    <pre>{props.stat.searchLogs.map((log) => `${log.name}: ${log.val}`).join('\n')}</pre>
                  </div>
                </div>
              )}

              {!!props.stat.startTime &&
                <div className='mt-10'>
                  <span>First Response Time - {dateFormat(props.stat.startTime, 'dd/MM/yyyy HH:mm:ss')}</span>
                </div>
              }
              {!!props.stat.endTime &&
                <div className='mt-10'>
                  <span>Last Response Time - {dateFormat(props.stat.endTime, 'dd/MM/yyyy HH:mm:ss')}</span>
                </div>
              }
              {!!props.stat.startTime && !!props.stat.endTime &&
                <div className='mt-10'>
                  <span>Response Time - {((props.stat.endTime.getTime() - props.stat.startTime.getTime()) / 1000).toFixed(1)} seconds</span>
                </div>
              }
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(ResultHotelConfig, 'ResultHotelConfig');
